import * as React from 'react';
import { JsonFormsDispatch } from '@jsonforms/react';
import { Select, Divider, StackLayout } from '@leagueplatform/genesis-core';
import type {
  ControlComponent,
  EntityFormControlProps,
} from '@web-config-app/entity-form';
import {
  CompoundPrimitiveGroupFieldset,
  PrimitiveControlFormField,
  useEntityFormControl,
  useCombinatorSchemas,
  getSelectOptionsWithPlaceholder,
} from '@web-config-app/entity-form';

export const SelectableDiscriminatorCombinatorControlContents = (
  controlProps: EntityFormControlProps,
) => {
  const {
    path,
    rootSchema,
    data,
    handleChange,
    visible,
    enabled,
    renderers,
    cells,
    inputStatus,
    combinatorProperties,
  } = controlProps;
  // TODO: Review this in future for performance optimizations

  const {
    combinatorInputWrapperOptions,
    combinatorDiscriminator = '',
    combinatorSelectOptions,
    combinatorPlaceholder,
  } = combinatorProperties ?? {};

  const { selectedSubSchema, detailUiSchema, createSelectedSubSchemaValue } =
    useCombinatorSchemas({
      combinatorProperties,
      rootSchema,
      data,
    });

  const selectOptionsWithPlaceholder = getSelectOptionsWithPlaceholder({
    enumOptions: combinatorSelectOptions,
    enumPlaceholder: combinatorPlaceholder,
  });

  const value = combinatorDiscriminator && data?.[combinatorDiscriminator];

  return (
    <StackLayout spacing="$half" horizontalAlignment="stretch">
      <PrimitiveControlFormField
        {...controlProps}
        {...combinatorInputWrapperOptions}
        id={`${combinatorDiscriminator}-select-input`}
      >
        <Select
          // if there is no value, we set it to an empty string to select the placeholder
          // this is necessary because the Select component does not accept undefined values
          // and we want to show the placeholder when no value is selected
          value={value ?? ''}
          onChange={(evt) => {
            const selectedDiscriminator = evt.currentTarget.value;
            handleChange(
              path,
              createSelectedSubSchemaValue?.(selectedDiscriminator),
            );
          }}
          readOnly={!enabled}
          inputStatus={inputStatus}
          {...selectOptionsWithPlaceholder}
        />
      </PrimitiveControlFormField>
      {selectedSubSchema && (
        <>
          <Divider />
          <JsonFormsDispatch
            visible={visible}
            enabled={enabled}
            schema={selectedSubSchema}
            uischema={detailUiSchema}
            path={path}
            renderers={renderers}
            cells={cells}
          />
        </>
      )}
    </StackLayout>
  );
};

export const SelectableDiscriminatorCombinatorControl: ControlComponent = (
  props,
) => {
  const entityFormControlProps = useEntityFormControl(props);
  const { hint, label, banner } = entityFormControlProps;
  return (
    <CompoundPrimitiveGroupFieldset hint={hint} label={label} banner={banner}>
      <SelectableDiscriminatorCombinatorControlContents
        {...entityFormControlProps}
      />
    </CompoundPrimitiveGroupFieldset>
  );
};
